import React, { useEffect, useState } from 'react';
import { api, fmt, fmtDate } from '../utils/api';
import Topbar from '../components/Topbar';
import { Badge, StatusBadge, EmptyState, Spinner, SearchInput, useToast, Modal } from '../components/UI';
export default function Approvals({ user, directorView }) {
  const [declarations, setDeclarations] = useState([]);
  const [royalties, setRoyalties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('pending');
  const [showToast, Toast] = useToast();
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const isAdmin = user?.role === 'admin' || user?.role === 'hq_support';
  const load = () => {
    Promise.all([api.getDeclarations(directorView), api.getRoyalties()]).then(([d, r]) => {
      setDeclarations(d || []);
      setRoyalties(r || []);
      setLoading(false);
    });
  };
  useEffect(() => { load(); }, []);
  async function approve(d) {
    await api.approveDeclaration(d.id);
    if (d.paymentType === 'submit_and_pay' && parseFloat(d.royalty) > 0) {
      const existingRoyalties = await api.getRoyalties();
      const alreadyExists = (existingRoyalties||[]).some(r =>
        (r.franchiseId===d.franchiseId||r.franchise_id===d.franchiseId) && r.month===d.month
      );
      if (!alreadyExists) {
        await api.createRoyalty({
          franchiseId: d.franchiseId,
          franchiseName: d.franchiseName,
          month: d.month,
          revenue: d.income,
          royalty: d.royalty,
          paymentDate: new Date().toISOString().split('T')[0],
          source: 'manual',
          paymentMethod: 'manual',
        });
      }
      api.createXeroInvoiceForRoyalty({
        franchiseId: d.franchiseId,
        franchiseName: d.franchiseName,
        month: d.month,
        royalty: d.royalty,
      }).catch(() => {});
    }
    await api.logActivity('Approved declaration', `${d.franchiseName} — ${d.month}`);
    showToast(d.paymentType === 'submit_and_pay' ? 'Approved & royalty recorded!' : 'Approved');
    load();
  }
  async function saveEdit() {
    await api.updateDeclaration(editModal.id, editForm);
    await api.logActivity('Edited declaration', `${editModal.franchiseName} — ${editModal.month}`);
    showToast('Updated'); setEditModal(null); load();
  }
  const pending = declarations.filter(d => d.status === 'pending' && !d.editRequested);
  const editRequests = declarations.filter(d => d.editRequested);
  const amendmentsPending = declarations.filter(d => d.status === 'approved' && d.amendedIncome !== null && d.amendedIncome !== undefined && !d.amendmentSettled);
  const counts = {
    pending: pending.length,
    edit: editRequests.length,
    amendments: amendmentsPending.length,
    approved: declarations.filter(d=>d.status==='approved').length,
    rejected: declarations.filter(d=>d.status==='rejected').length,
  };
  const getDeclList = () => {
    let list = [];
    if (filter === 'pending') list = pending;
    else if (filter === 'edit') list = editRequests;
    else if (filter === 'amendments') list = amendmentsPending;
    else if (filter === 'approved') list = declarations.filter(d=>d.status==='approved');
    else if (filter === 'rejected') list = declarations.filter(d=>d.status==='rejected');
    else list = declarations;
    const q = search.toLowerCase();
    if (!q) return list;
    return list.filter(d =>
      (d.franchiseName||'').toLowerCase().includes(q) ||
      (d.franchiseeName||'').toLowerCase().includes(q) ||
      (d.month||'').toLowerCase().includes(q)
    );
  };
  if (loading) return <div style={{ marginLeft:'var(--sidebar-width)' }}><Topbar title="Approvals" /><Spinner /></div>;
  const list = getDeclList();
  return (
    <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      <Topbar title="Approvals" subtitle="Review and approve franchise declarations" />
      {Toast}
      <div style={{ padding:24 }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10, marginBottom:16 }}>
          {[
            { key:'pending', label:'Pending', color:'var(--amber)' },
            { key:'edit', label:'Edit Requests', color:'var(--blue)' },
            { key:'amendments', label:'Amendments', color:'var(--red)' },
            { key:'approved', label:'Approved', color:'var(--green)' },
            { key:'rejected', label:'Rejected', color:'var(--text-tertiary)' },
          ].map(s => (
            <button key={s.key} onClick={() => setFilter(s.key)} className="card" style={{
              padding:'12px 16px', textAlign:'left', border: filter===s.key?`2px solid ${s.color}`:'1px solid var(--border)',
              cursor:'pointer', background: filter===s.key?'var(--blue-bg)':'#fff',
            }}>
              <div style={{ fontSize:11, color:'var(--text-tertiary)', marginBottom:4 }}>{s.label}</div>
              <div style={{ fontSize:22, fontWeight:700, color:s.color }}>{counts[s.key]}</div>
            </button>
          ))}
        </div>
        <div style={{ marginBottom:14, maxWidth:380 }}>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by branch or month..." />
        </div>
        {list.length === 0 ? (
          <div className="card" style={{ padding:48 }}>
            <EmptyState icon="ti-checks" message={filter === 'pending' ? 'No pending approvals' : 'Nothing here'} />
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {list.sort((a,b) => (b.month||'').localeCompare(a.month||'')).map(d => (
              <DeclarationCard
                key={d.id}
                d={d}
                isAdmin={isAdmin}
                royalties={royalties}
                onApprove={() => approve(d)}
                onReject={() => { setRejectModal(d); setRejectReason(''); }}
                onEdit={() => { setEditModal(d); setEditForm({ income:d.income, rent:d.rent, salaries:d.salaries, royalty:d.royalty, otherExpenses:d.otherExpenses }); }}
                onDelete={async () => {
                  if (!window.confirm(`Delete declaration for ${d.franchiseName} (${d.month})?`)) return;
                  await api.deleteDeclaration(d.id);
                  await api.logActivity('Deleted declaration', `${d.franchiseName} — ${d.month}`);
                  showToast('Deleted'); load();
                }}
                onConfirmPayment={async () => {
                  await api.confirmPayment(d.id);
                  await api.logActivity('Confirmed payment', `${d.franchiseName} — ${d.month}`);
                  showToast('Payment confirmed'); load();
                }}
                onApproveEdit={async () => {
                  await api.approveEditRequest(d.id);
                  await api.logActivity('Approved edit request', `${d.franchiseName} — ${d.month}`);
                  showToast('Edit request approved'); load();
                }}
                onRejectEdit={() => { setRejectModal({...d, isEdit:true}); setRejectReason(''); }}
                onSettleAmendment={async () => {
                  await api.markAmendmentSettled(d.id);
                  await api.logActivity('Amendment settled', `${d.franchiseName} — ${d.month}`);
                  showToast('Amendment settled'); load();
                }}
                showToast={showToast}
                onReload={load}
              />
            ))}
          </div>
        )}
      </div>
      {editModal && (
        <Modal title={`Edit — ${editModal.branchLocation||editModal.franchiseName} ${editModal.month}`}
          onClose={() => setEditModal(null)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setEditModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveEdit}>Save Changes</button>
          </>}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            {[['INCOME','income'],['RENT','rent'],['SALARIES','salaries'],['ROYALTY','royalty'],['OTHER EXPENSES','otherExpenses']].map(([l,k]) => (
              <div key={k}><label>{l} (£)</label>
                <input type="number" value={editForm[k]||''} onChange={e=>setEditForm(f=>({...f,[k]:e.target.value}))} />
              </div>
            ))}
          </div>
          <div style={{ marginTop:12, padding:'10px 14px', background:'var(--blue-bg)', borderRadius:'var(--r-md)', fontSize:12 }}>
            Net: <strong style={{ color: ((parseFloat(editForm.income)||0)-((parseFloat(editForm.rent)||0)+(parseFloat(editForm.salaries)||0)+(parseFloat(editForm.royalty)||0)+(parseFloat(editForm.otherExpenses)||0)))>=0?'var(--green)':'var(--red)' }}>
              {fmt((parseFloat(editForm.income)||0)-((parseFloat(editForm.rent)||0)+(parseFloat(editForm.salaries)||0)+(parseFloat(editForm.royalty)||0)+(parseFloat(editForm.otherExpenses)||0)))}
            </strong>
          </div>
        </Modal>
      )}
      {rejectModal && (
        <Modal title={rejectModal.isEdit ? 'Reject Edit Request' : 'Reject Declaration'}
          onClose={() => setRejectModal(null)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setRejectModal(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={async () => {
              if (!rejectReason.trim()) { showToast('Please provide a reason'); return; }
              if (rejectModal.isEdit) {
                await api.rejectEditRequest(rejectModal.id, rejectReason);
                await api.logActivity('Rejected edit request', `${rejectModal.franchiseName} — ${rejectModal.month}`);
              } else {
                await api.rejectDeclaration(rejectModal.id, rejectReason);
                await api.logActivity('Rejected declaration', `${rejectModal.franchiseName} — ${rejectModal.month} — Reason: ${rejectReason}`);
              }
              showToast('Rejected'); setRejectModal(null); setRejectReason(''); load();
            }}>Reject</button>
          </>}>
          <div style={{ marginBottom:8, fontSize:13, color:'var(--text-secondary)' }}>
            {rejectModal.branchLocation||rejectModal.franchiseName} · {rejectModal.month}
          </div>
          <label>REASON FOR REJECTION</label>
          <textarea value={rejectReason} onChange={e=>setRejectReason(e.target.value)} rows={3} placeholder="Provide a reason..." style={{ resize:'none' }} />
        </Modal>
      )}
    </div>
  );
}
function DeclarationCard({ d, isAdmin, royalties, onApprove, onReject, onEdit, onDelete, onConfirmPayment, onApproveEdit, onRejectEdit, onSettleAmendment, showToast, onReload }) {
  const [expanded, setExpanded] = useState(false);
  const exp = (parseFloat(d.rent)||0)+(parseFloat(d.salaries)||0)+(parseFloat(d.royalty)||0)+(parseFloat(d.otherExpenses)||0);
  const net = (parseFloat(d.income)||0) - exp;
  const margin = d.income > 0 ? (net/parseFloat(d.income)*100).toFixed(1) : 0;
  const royaltyPaid = royalties.some(r => (r.franchiseId===d.franchiseId || r.franchise_id===d.franchiseId) && r.month===d.month);
  const hasAmendment = d.amendedIncome !== null && d.amendedIncome !== undefined;
  const amendedExp = hasAmendment ? ((parseFloat(d.amendedRent)||0)+(parseFloat(d.amendedSalaries)||0)+(parseFloat(d.amendedRoyalty)||0)+(parseFloat(d.amendedOtherExpenses)||0)) : 0;
  const amendedNet = hasAmendment ? ((parseFloat(d.amendedIncome)||0) - amendedExp) : 0;
  const royaltyDiff = hasAmendment ? ((parseFloat(d.amendedRoyalty)||0) - (parseFloat(d.royalty)||0)) : 0;
  return (
    <div className="card" style={{ overflow:'hidden', border: d.editRequested?'1px solid var(--amber)': hasAmendment&&!d.amendmentSettled?'1px solid var(--red)':'1px solid var(--border)' }}>
      <div style={{ padding:'14px 18px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:38, height:38, borderRadius:'var(--r-md)', background:'var(--blue-bg)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'var(--navy)' }}>
            {(d.branchLocation||d.franchiseName||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight:600, fontSize:14 }}>{d.branchLocation || d.franchiseName}
              {d.branchLocation && d.branchLocation!==d.franchiseName ? <span style={{ fontSize:11, fontWeight:400, color:'var(--text-tertiary)', marginLeft:6 }}>({d.franchiseName})</span> : null}
            </div>
            <div style={{ fontSize:12, color:'var(--text-tertiary)' }}>{d.franchiseeName} · {d.month}</div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <div style={{ display:'flex', gap:12 }}>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:10, color:'var(--text-tertiary)' }}>INCOME</div>
              <div style={{ fontSize:13, fontWeight:500, fontFamily:'DM Mono,monospace' }}>{fmt(d.income)}</div>
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:10, color:'var(--text-tertiary)' }}>ROYALTY</div>
              <div style={{ fontSize:13, fontWeight:500, fontFamily:'DM Mono,monospace', color:'var(--navy)' }}>{fmt(d.royalty)}</div>
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:10, color:'var(--text-tertiary)' }}>NET</div>
              <div style={{ fontSize:14, fontWeight:700, fontFamily:'DM Mono,monospace', color:net>=0?'var(--green)':'var(--red)' }}>{fmt(net)}</div>
            </div>
          </div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            {d.editRequested && <Badge type="amber">Edit Request</Badge>}
            {hasAmendment && !d.amendmentSettled && <Badge type="red">Amendment Pending</Badge>}
            {hasAmendment && d.amendmentSettled && <Badge type="green">Amendment Settled</Badge>}
            {d.paymentType === 'submit_and_pay' && (
              <Badge type={royaltyPaid || d.paymentStatus==='confirmed' ? 'green' : 'amber'}>
                {royaltyPaid || d.paymentStatus==='confirmed' ? '✓ Paid' : 'Awaiting Payment'}
              </Badge>
            )}
            <StatusBadge status={d.status} />
          </div>
        </div>
      </div>
      {d.editRequested && (
        <div style={{ margin:'0 18px', marginBottom:10 }}>
          {d.editRequestNote && (
            <div style={{ padding:'10px 14px', background:'var(--amber-bg)', borderRadius:'var(--r-md) var(--r-md) 0 0', fontSize:12, color:'var(--amber-dark)', borderBottom:'1px solid rgba(217,119,6,0.15)' }}>
              <strong>Edit request:</strong> {d.editRequestNote}
            </div>
          )}
          {(d.amendedIncome != null || d.amendedRoyalty != null || d.amendedRent != null) && (
            <div style={{ padding:'10px 14px', background:'rgba(37,99,235,0.05)', borderRadius: d.editRequestNote ? '0 0 var(--r-md) var(--r-md)' : 'var(--r-md)', border:'1px solid rgba(37,99,235,0.15)', fontSize:12 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
                {[['Income', d.income, d.amendedIncome],['Rent', d.rent, d.amendedRent],['Salaries', d.salaries, d.amendedSalaries],['Royalty', d.royalty, d.amendedRoyalty],['Other', d.otherExpenses, d.amendedOtherExpenses]].map(([label, orig, prop]) => (
                  prop !== null && prop !== undefined ? (
                    <div key={label} style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <span style={{ color:'var(--text-tertiary)' }}>{label}:</span>
                      <span style={{ textDecoration:'line-through', color:'var(--text-tertiary)' }}>{fmt(orig)}</span>
                      <span style={{ fontWeight:600, color:'var(--blue)' }}>→ {fmt(prop)}</span>
                    </div>
                  ) : null
                ))}
              </div>
              {(() => {
                const origRoy = parseFloat(d.royalty)||0;
                const propRoy = d.amendedRoyalty != null ? parseFloat(d.amendedRoyalty)||0 : origRoy;
                const diff = propRoy - origRoy;
                if (diff === 0) return null;
                return (
                  <div style={{ padding:'8px 12px', borderRadius:'var(--r-sm)', background:diff>0?'var(--amber-bg)':'var(--green-bg)', fontWeight:600, fontSize:12 }}>
                    {diff > 0 ? `⚠ Additional royalty if approved: ${fmt(diff)}` : `✓ Royalty reduction if approved: ${fmt(Math.abs(diff))}`}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}
      {hasAmendment && !d.amendmentSettled && (
        <div style={{ margin:'0 18px', padding:'10px 14px', background:'rgba(239,68,68,0.05)', borderRadius:'var(--r-md)', fontSize:12, marginBottom:10, border:'1px solid rgba(239,68,68,0.15)' }}>
          <div style={{ fontWeight:600, marginBottom:6, color:'var(--red)' }}>Amendment Required</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            {[['Income', d.income, d.amendedIncome],['Rent', d.rent, d.amendedRent],['Salaries', d.salaries, d.amendedSalaries],['Royalty', d.royalty, d.amendedRoyalty],['Other', d.otherExpenses, d.amendedOtherExpenses]].map(([label, orig, amend]) => (
              amend !== null && amend !== undefined ? (
                <div key={label} style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <span style={{ color:'var(--text-tertiary)' }}>{label}:</span>
                  <span style={{ textDecoration:'line-through', color:'var(--text-tertiary)' }}>{fmt(orig)}</span>
                  <span style={{ fontWeight:600, color:'var(--navy)' }}>→ {fmt(amend)}</span>
                </div>
              ) : null
            ))}
          </div>
          <div style={{ marginTop:8 }}>
            Royalty difference: <strong style={{ color: royaltyDiff > 0 ? 'var(--red)' : 'var(--green)' }}>
              {royaltyDiff > 0 ? '+' : ''}{fmt(royaltyDiff)}
            </strong>
          </div>
        </div>
      )}
      {expanded && (
        <div style={{ padding:'0 18px 14px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:8 }}>
            {[['Rent',d.rent],['Salaries',d.salaries],['Royalty',d.royalty],['Other',d.otherExpenses],['Margin',`${margin}%`]].map(([l,v]) => (
              <div key={l} style={{ padding:'8px 12px', background:'var(--blue-bg)', borderRadius:'var(--r-sm)' }}>
                <div style={{ fontSize:10, color:'var(--text-tertiary)' }}>{l}</div>
                <div style={{ fontSize:13, fontWeight:500 }}>{typeof v === 'string' ? v : fmt(v)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ padding:'10px 18px', borderTop:'1px solid var(--border)', display:'flex', gap:6, flexWrap:'wrap', alignItems:'center', justifyContent:'space-between', background:'#fafbfd' }}>
        <button onClick={() => setExpanded(e=>!e)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, color:'var(--text-tertiary)', display:'flex', alignItems:'center', gap:4 }}>
          <i className={`ti ti-chevron-${expanded?'up':'down'}`} style={{ fontSize:13 }} />
          {expanded ? 'Less detail' : 'More detail'}
        </button>
        {isAdmin && (
          <div style={{ display:'flex', gap:6 }}>
            {d.paymentType === 'submit_and_pay' && d.paymentStatus !== 'confirmed' && (
              <button className="btn btn-sm" style={{ background:'var(--blue-badge-bg)', color:'var(--blue)', border:'none', fontSize:12 }} onClick={onConfirmPayment}>
                Confirm Payment
              </button>
            )}
            {d.editRequested && (
              <>
                <button className="btn btn-sm" style={{ background:'var(--green-bg)', color:'var(--green)', border:'1px solid rgba(15,110,86,0.2)', fontSize:12 }} onClick={onApproveEdit}>
                  ✓ Allow Edit
                </button>
                <button className="btn btn-sm btn-danger" style={{ fontSize:12 }} onClick={onRejectEdit}>
                  ✕ Deny Edit
                </button>
              </>
            )}
            {hasAmendment && !d.amendmentSettled && (
              <button className="btn btn-sm" style={{ background:'var(--green-bg)', color:'var(--green)', border:'1px solid rgba(15,110,86,0.2)', fontSize:12 }} onClick={onSettleAmendment}>
                Mark Settled
              </button>
            )}
            {d.status === 'pending' && !d.editRequested && (
              <>
                <button className="btn btn-sm" style={{ background:'var(--green-bg)', color:'var(--green)', border:'1px solid rgba(15,110,86,0.2)', fontSize:12 }} onClick={onApprove}>
                  ✓ Approve
                </button>
                <button className="btn btn-sm btn-danger" style={{ fontSize:12 }} onClick={onReject}>✕ Reject</button>
              </>
            )}
            <button className="btn btn-sm btn-secondary" style={{ fontSize:12 }} onClick={onEdit}>Edit</button>
            <button className="btn btn-sm btn-danger" style={{ fontSize:12 }} onClick={onDelete}>Delete</button>
          </div>
        )}
      </div>
    </div>
  );
}
